import React from 'react';
import { css } from '@emotion/css';
import { Link } from 'is24-corecss';
import { IconS24ChevronRight24 } from '@is24/cosma-ui-icons';
import { Modal } from '../../../../common/display/modal/Modal';
import { Benefit } from '../../../../../@types/benefits';
import RelocationServicePng from './images/relocation.png';
import RelocationServiceWebp from './images/relocation.webp';
import RelocationServiceWebpMobile from './images/relocation_mobile.webp';
import RelocationServicePngMobile from './images/relocation_mobile.png';
import ImageOnePlusPng from './images/plus/section1@3x.png';
import ImageOnePlusWebp from './images/plus/section1@3x.webp';
import ImageTwoPlusPng from './images/plus/section2@3x.png';
import ImageTwoPlusWebp from './images/plus/section2@3x.webp';
import ImageThreePlusPng from './images/plus/section3@3x.png';
import ImageThreePlusWebp from './images/plus/section3@3x.webp';

import type { BenefitModalProps } from '../../../../../@types/benefitModalProps';
import { OrderedList } from '../../../../common/display/orderedList/OrderedList';
import { ImageSource } from '../../../../common/display/imageSource/ImageSource';
import { useGetHasProduct } from '../../../../../customHooks/useGetHasProduct.hook';
import { ProductType } from '../../../../../@types/product';
import { FAQTab } from '../../../../common/display/faqTab/FAQTab';
import { APP_CONFIG } from '../../../../../config';
import { report } from '../../../../../tracking/tracking';
import { OverviewTrackingEvents } from '../../../../../tracking/trackingEvents';
import { useHasMeinPlus } from '../../../../../customHooks/useHasMeinPlus.hook';
import { Msg, useIntl } from '@/i18n';
import { PlusBadge } from '@/components/common/display/plusBadge/PlusBadge';

const styles = {
  overviewTab: { maxWidth: 530 },
  miterplusCta: { paddingTop: '12px', maxWidth: 530 },
  imageWrapper: {
    minHeight: '106px',
    display: 'flex',
    alignItems: 'center',
  },
  i: {
    marginRight: '8px',
    fontSize: '16px',
  },
};

const OverviewTab = () => {
  const isEntitledPlusRent = useGetHasProduct(ProductType.MieterPlus);

  return (
    <div style={styles.overviewTab}>
      <div className='font-heading-xsmall-regular padding-bottom-l'>
        <Msg id='benefits.relocationService.modal.overview.paragraph1' defaultMessage='Support for legal issues...' />
      </div>
      <div className='padding-bottom-l'>
        <Msg id='benefits.relocationService.modal.overview.paragraph2' defaultMessage='If you have a dispute...' />
      </div>
      <ul className='list-check list-spacing-s'>
        <li>
          <Msg id='benefits.relocationService.modal.overview.bullet1' defaultMessage='The legal...' />
        </li>
        <li>
          <Msg id='benefits.relocationService.modal.overview.bullet2' defaultMessage='Court costs...' />
        </li>
        <li>
          <Msg id='benefits.relocationService.modal.overview.bullet3' defaultMessage='Procedural costs...' />
        </li>
      </ul>

      {!isEntitledPlusRent && (
        <div style={styles.miterplusCta}>
          <ul className='list-check list-spacing-s margin-bottom-m'>
            <Msg
              id='benefits.relocationService.modal.overview.alt.bullet1'
              defaultMessage='Exclusive & free...'
              values={{
                MieterPlusLogo: <PlusBadge variant='rent' />,
              }}
            />
          </ul>
          <Link
            href={
              '/meinkonto/premium-mitgliedschaft/?cmp_id=10-051537&cmp_name=residential_profile&cmp_position=residential_benefits&cmp_creative=relocation_cta'
            }
            className='margin-top-l'
            variant='default'
            endElement={<IconS24ChevronRight24 />}
          >
            <Msg id='benefits.relocationService.modal.overview.alt.bullet2' defaultMessage='If you have a dispute...' />
          </Link>
        </div>
      )}
    </div>
  );
};

const HowItWorksTab = () => {
  const { t } = useIntl();
  const Item = OrderedList.Item;

  return (
    <div className='margin-bottom-xxl'>
      <OrderedList>
        <Item
          image={
            <div className={css(styles.imageWrapper)}>
              <ImageSource
                image={ImageOnePlusPng}
                imageWebp={ImageOnePlusWebp}
                imageAlt={t({
                  id: 'benefits.relocationService.modal.howItWorks.image1',
                  defaultMessage: 'Scale',
                })}
                sizes='100px'
              />
            </div>
          }
          text={t({
            id: 'benefits.relocationService.modal.howItWorks.paragraph1',
            defaultMessage: 'Get your documents after activation',
          })}
        />
        <Item
          image={
            <div className={css(styles.imageWrapper)}>
              <ImageSource
                image={ImageTwoPlusPng}
                imageWebp={ImageTwoPlusWebp}
                imageAlt={t({
                  id: 'benefits.relocationService.modal.howItWorks.image2',
                  defaultMessage: 'Scale',
                })}
                sizes='100px'
              />
            </div>
          }
          text={t({
            id: 'benefits.relocationService.modal.howItWorks.paragraph2',
            defaultMessage: 'Get your documents after activation',
          })}
        />
        <Item
          image={
            <div className={css(styles.imageWrapper)}>
              <ImageSource
                image={ImageThreePlusPng}
                imageWebp={ImageThreePlusWebp}
                imageAlt={t({
                  id: 'benefits.relocationService.modal.howItWorks.image3',
                  defaultMessage: 'Phone',
                })}
                sizes='100px'
              />
            </div>
          }
          text={t({
            id: 'benefits.relocationService.modal.howItWorks.paragraph3',
            defaultMessage: 'Get your documents after activation',
          })}
        />
      </OrderedList>
    </div>
  );
};

const ButtonArea = ({ isMeinPlus }: { isMeinPlus: boolean }) => {
  const handleSubmitCallback = React.useCallback(async () => {
    await report(OverviewTrackingEvents.RELOCATION_SERVICE.FUNNEL_SUBMIT, isMeinPlus);
  }, [isMeinPlus]);

  return (
    <div className='align-center'>
      <a className='button-secondary button-rounded' href={APP_CONFIG.relocationUrl} onClick={handleSubmitCallback}>
        <Msg id='benefits.relocationService.modal.linkButton' defaultMessage='Discover Mieter Plus' />
      </a>
    </div>
  );
};

export const RelocationService = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();
  const isMeinPlus = useHasMeinPlus();

  return (
    <Modal
      title={t({ id: 'benefits.relocationService.modal.headline', defaultMessage: 'Legal insurance' })}
      isVisible={isVisible}
      image={RelocationServicePng}
      imageWebp={RelocationServiceWebp}
      imageMobile={RelocationServicePngMobile}
      imageMobileWebp={RelocationServiceWebpMobile}
      imageAlt={t({
        id: 'benefits.relocationService.modal.image',
        defaultMessage: 'New build in sunshine',
      })}
      imageUseFullWidth={true}
      tabsContent={[
        <OverviewTab key='benefits.relocationService.modal.overview' />,
        <HowItWorksTab key='benefits.relocationService.modal.howItWorks' />,
        <FAQTab
          key='benefits.relocationService.modal.faq'
          benefit='relocationService'
          includeCustomerService={false}
        />,
      ]}
      buttonArea={<ButtonArea isMeinPlus={isMeinPlus} />}
      benefit={Benefit.RELOCATION_SERVICE}
      onClose={onClose}
    />
  );
};
